// web/src/copilot/NoteConfirmCard.tsx
//
// HIL confirmation card rendered inline in the chat thread when the agent
// drafts a note via the `create_note` action. The user can edit the body
// and approve (Save) or reject (Cancel / ESC). The 5-minute timeout is
// handled by the caller (the action's renderAndWaitForResponse handler).
//
// Target shape mirrors the PendingContext kind/id/title triple used
// elsewhere; passing `target=null` indicates a freeform note (no
// attachment) and the card label reflects that.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { PendingContextValue } from "./PendingContextProvider";

interface NoteConfirmCardProps {
  target: PendingContextValue | null;
  draft: string;
  onSave: (body: string) => void;
  onCancel: () => void;
}

function describeTarget(
  target: PendingContextValue | null,
  t: TFunction<"copilot">,
): string {
  if (!target) return t("noteConfirm.target.freeform");
  const label = target.title?.trim() ? target.title : target.id;
  switch (target.kind) {
    case "cluster":
      return t("noteConfirm.target.cluster", { label });
    case "analysis":
      return t("noteConfirm.target.analysis", { label });
    case "material":
      return t("noteConfirm.target.material", { label });
    default:
      return t("noteConfirm.target.freeform");
  }
}

export function NoteConfirmCard({
  target,
  draft,
  onSave,
  onCancel,
}: NoteConfirmCardProps) {
  const { t } = useTranslation("copilot");
  const [body, setBody] = useState(draft);

  // Keep body in sync if the agent re-renders with a different draft (rare —
  // happens if the action restarts within the same chat turn).
  useEffect(() => {
    setBody(draft);
  }, [draft]);

  // ESC anywhere in the document cancels the confirm. Scoped to the lifetime
  // of the card so a closed/unmounted card doesn't intercept future ESC.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="rounded-md border bg-card p-3 space-y-3">
      <div className="text-xs text-muted-foreground">
        {t("noteConfirm.title")}
      </div>
      <div className="text-sm font-medium">{describeTarget(target, t)}</div>
      <Textarea
        aria-label={t("noteConfirm.bodyLabel")}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
      />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          {t("noteConfirm.cancel")}
        </Button>
        <Button size="sm" onClick={() => onSave(body)}>
          {t("noteConfirm.save")}
        </Button>
      </div>
    </div>
  );
}
